import { supabase } from './supabase';
import type {
  Business,
  Customer,
  Card,
  Payment,
  Expense,
  DailyRate,
  CardType,
  ExpenseCategory,
  CustomerWithCards,
  PaymentWithDetails,
  UserPreferences,
} from '../types/database';
import { CARD_TYPE_DEFAULTS } from '../types/database';
import { getFirstDayOfCurrentMonth } from './dateUtils';

// PIN Authentication Functions
export async function verifyPin(pin: string): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase.rpc('verify_user_pin', {
    input_pin: pin
  });

  if (error) throw error;
  return data === true;
}

export async function updatePin(oldPin: string, newPin: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // First verify old PIN
  const isValid = await verifyPin(oldPin);
  if (!isValid) throw new Error('Invalid current PIN');

  // Update to new PIN
  const { error } = await supabase.rpc('update_user_pin', {
    new_pin: newPin
  });

  if (error) throw error;
}

export async function getBusinesses(): Promise<Business[]> {
  const { data, error } = await supabase
    .from('businesses')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function createBusiness(
  name: string,
  stripeKeyEncrypted: string,
  bankRate?: number,
  defaultBlackMarketRate?: number
): Promise<Business> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('businesses')
    .insert({
      user_id: user.id,
      name,
      stripe_key_encrypted: stripeKeyEncrypted,
      bank_rate: bankRate || 15.42,
      default_black_market_rate: defaultBlackMarketRate || 20.25,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateBusiness(
  id: string,
  updates: Partial<Omit<Business, 'id' | 'user_id' | 'created_at'>>
): Promise<Business> {
  const { data, error } = await supabase
    .from('businesses')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteBusiness(id: string): Promise<void> {
  const { error } = await supabase.from('businesses').delete().eq('id', id);
  if (error) throw error;
}

export async function getCustomers(businessId: string): Promise<CustomerWithCards[]> {
  const { data, error } = await supabase
    .from('customers')
    .select(`
      *,
      cards (*)
    `)
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data as any[]).map(customer => ({
    ...customer,
    cards: customer.cards || [],
  }));
}

export async function createCustomer(
  businessId: string,
  name: string,
  email?: string,
  phone?: string,
  stripeCustomerId?: string
): Promise<Customer> {
  const { data, error } = await supabase
    .from('customers')
    .insert({
      business_id: businessId,
      name,
      email: email || null,
      phone: phone || null,
      stripe_customer_id: stripeCustomerId || null,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateCustomer(
  id: string,
  updates: Partial<Omit<Customer, 'id' | 'business_id' | 'created_at'>>
): Promise<Customer> {
  const { data, error } = await supabase
    .from('customers')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteCustomer(id: string): Promise<void> {
  console.log('Attempting to delete customer:', id);
  const { error, data } = await supabase.from('customers').delete().eq('id', id).select();
  if (error) {
    console.error('Failed to delete customer:', error);
    throw error;
  }
  console.log('Successfully deleted customer:', id, 'Rows affected:', data?.length);
}

export async function findCustomerByStripeId(
  businessId: string,
  stripeCustomerId: string
): Promise<Customer | null> {
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('business_id', businessId)
    .eq('stripe_customer_id', stripeCustomerId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function findCustomerByEmail(
  businessId: string,
  email: string
): Promise<Customer | null> {
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('business_id', businessId)
    .eq('email', email)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function createCard(
  customerId: string,
  cardType: CardType,
  limitUsd: number
): Promise<Card> {
  const { data, error } = await supabase
    .from('cards')
    .insert({
      customer_id: customerId,
      card_type: cardType,
      limit_usd: limitUsd,
      remaining_balance_usd: limitUsd,
      last_reset_date: getFirstDayOfCurrentMonth(),
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateCard(
  id: string,
  updates: Partial<Omit<Card, 'id' | 'customer_id' | 'created_at'>>
): Promise<Card> {
  const { data, error } = await supabase
    .from('cards')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteCard(id: string): Promise<void> {
  const { error } = await supabase.from('cards').delete().eq('id', id);
  if (error) throw error;
}

export async function resetCardBalance(cardId: string, newLimit: number): Promise<Card> {
  const { data, error } = await supabase
    .from('cards')
    .update({
      remaining_balance_usd: newLimit,
      last_reset_date: getFirstDayOfCurrentMonth(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', cardId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function checkAndResetCards(businessId: string): Promise<void> {
  const firstOfMonth = getFirstDayOfCurrentMonth();

  const { data: staleCards, error } = await supabase
    .from('cards')
    .select('id, limit_usd, customer_id, last_reset_date')
    .lt('last_reset_date', firstOfMonth)
    .in(
      'customer_id',
      (await supabase
        .from('customers')
        .select('id')
        .eq('business_id', businessId)
      ).data?.map(c => c.id) || []
    );

  if (error) throw error;
  if (!staleCards || staleCards.length === 0) return;

  for (const card of staleCards) {
    await resetCardBalance(card.id, card.limit_usd);
  }
}

export async function getPayments(
  businessId: string,
  dateRange?: { start: string; end: string }
): Promise<PaymentWithDetails[]> {
  let query = supabase
    .from('payments')
    .select(`
      *,
      customer:customers (*),
      card:cards (*)
    `)
    .eq('business_id', businessId);

  if (dateRange) {
    query = query.gte('payment_date', dateRange.start).lte('payment_date', dateRange.end);
  }

  const { data, error } = await query.order('payment_date', { ascending: false });

  if (error) throw error;
  return (data as any[]).map(payment => ({
    ...payment,
    customer: payment.customer,
    card: payment.card,
  }));
}

export async function createPayment(
  businessId: string,
  customerId: string,
  amountUsd: number,
  stripeChargeId?: string,
  paymentDate?: string,
  cardId?: string
): Promise<Payment> {
  const { data, error } = await supabase
    .from('payments')
    .insert({
      business_id: businessId,
      customer_id: customerId,
      card_id: cardId || null,
      amount_usd: amountUsd,
      stripe_charge_id: stripeChargeId || null,
      payment_date: paymentDate || new Date().toISOString(),
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updatePayment(
  id: string,
  updates: Partial<Omit<Payment, 'id' | 'business_id' | 'created_at'>>
): Promise<Payment> {
  const { data, error } = await supabase
    .from('payments')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deletePayment(id: string): Promise<void> {
  const { error } = await supabase.from('payments').delete().eq('id', id);
  if (error) throw error;
}

export async function findPaymentByStripeChargeId(
  businessId: string,
  stripeChargeId: string
): Promise<(Payment & { card?: Card | null }) | null> {
  const { data, error } = await supabase
    .from('payments')
    .select('*, card:cards(*)')
    .eq('business_id', businessId)
    .eq('stripe_charge_id', stripeChargeId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function deductFromCard(cardId: string, amount: number): Promise<Card> {
  const { data: card, error: fetchError } = await supabase
    .from('cards')
    .select('*')
    .eq('id', cardId)
    .single();

  if (fetchError) throw fetchError;

  const newBalance = Math.max(0, card.remaining_balance_usd - amount);

  const { data, error } = await supabase
    .from('cards')
    .update({
      remaining_balance_usd: newBalance,
      updated_at: new Date().toISOString(),
    })
    .eq('id', cardId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getExpenses(
  businessId: string,
  dateRange?: { start: string; end: string }
): Promise<Expense[]> {
  let query = supabase
    .from('expenses')
    .select('*')
    .eq('business_id', businessId);

  if (dateRange) {
    query = query.gte('expense_date', dateRange.start).lte('expense_date', dateRange.end);
  }

  const { data, error } = await query.order('expense_date', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function createExpense(
  businessId: string,
  amountMvr: number,
  category: ExpenseCategory,
  note: string,
  expenseDate?: string
): Promise<Expense> {
  const { data, error } = await supabase
    .from('expenses')
    .insert({
      business_id: businessId,
      amount_mvr: amountMvr,
      category,
      note,
      expense_date: expenseDate || new Date().toISOString().split('T')[0],
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateExpense(
  id: string,
  updates: Partial<Omit<Expense, 'id' | 'business_id' | 'created_at'>>
): Promise<Expense> {
  const { data, error } = await supabase
    .from('expenses')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteExpense(id: string): Promise<void> {
  console.log('Attempting to delete expense:', id);
  const { error, data } = await supabase
    .from('expenses')
    .delete()
    .eq('id', id)
    .select();

  if (error) {
    console.error('Failed to delete expense:', error);
    throw new Error(error.message || 'Failed to delete expense');
  }
  console.log('Successfully deleted expense:', id, 'Rows affected:', data?.length);
}

export async function syncWiseFeesAsExpenses(
  businessId: string,
  transactions: Array<{
    referenceNumber: string;
    date: string;
    totalFees: { value: number; currency: string };
    details: { description?: string; type?: string; recipientName?: string };
  }>,
  bankRate: number
): Promise<number> {
  const feeTxns = transactions.filter(t => t.totalFees.value > 0);
  if (feeTxns.length === 0) return 0;

  const references = feeTxns.map(t => `wise_fee_${t.referenceNumber}`);
  const { data: existing } = await supabase
    .from('expenses')
    .select('fee_reference')
    .eq('business_id', businessId)
    .in('fee_reference', references);

  const existingRefs = new Set((existing || []).map(e => e.fee_reference));

  const newFees = feeTxns.filter(t => !existingRefs.has(`wise_fee_${t.referenceNumber}`));
  if (newFees.length === 0) return 0;

  const rows = newFees.map(t => ({
    business_id: businessId,
    amount_mvr: Math.round(t.totalFees.value * bankRate * 100),
    amount_usd: t.totalFees.value,
    expense_date: t.date.split('T')[0],
    category: 'Bank Fee' as ExpenseCategory,
    note: `Wise fee: ${t.details.description || t.details.recipientName || t.details.type || 'Transaction'} (${t.totalFees.currency} ${t.totalFees.value.toFixed(2)})`,
    fee_source: 'wise',
    fee_reference: `wise_fee_${t.referenceNumber}`,
  }));

  const { error } = await supabase.from('expenses').insert(rows);
  if (error) throw error;
  return rows.length;
}

export async function getDailyRate(businessId: string, date: string): Promise<DailyRate | null> {
  const { data, error } = await supabase
    .from('daily_rates')
    .select('*')
    .eq('business_id', businessId)
    .eq('rate_date', date)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function setDailyRate(
  businessId: string,
  date: string,
  blackMarketRate: number
): Promise<DailyRate> {
  const { data, error } = await supabase
    .from('daily_rates')
    .upsert({
      business_id: businessId,
      rate_date: date,
      black_market_rate: blackMarketRate,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export function mapStripeCardToType(brand: string | null, funding: string | null): CardType {
  const b = (brand || '').toLowerCase();
  const f = (funding || '').toLowerCase();

  if (b === 'visa') return f === 'credit' ? 'Visa Credit' : 'Visa Debit';
  if (b === 'mastercard') return f === 'credit' ? 'Mastercard Credit' : 'Mastercard Debit';
  if (b === 'amex' || b === 'american_express') return f === 'debit' ? 'Amex Debit' : 'Amex Credit';
  return 'Other';
}

export async function getCustomerCards(customerId: string): Promise<Card[]> {
  const { data, error } = await supabase
    .from('cards')
    .select('*')
    .eq('customer_id', customerId);

  if (error) throw error;
  return data || [];
}

export async function getPaymentsByCustomer(
  businessId: string,
  customerId: string
): Promise<PaymentWithDetails[]> {
  const { data, error } = await supabase
    .from('payments')
    .select(`
      *,
      customer:customers (*),
      card:cards (*)
    `)
    .eq('business_id', businessId)
    .eq('customer_id', customerId)
    .order('payment_date', { ascending: false });

  if (error) throw error;
  return (data as any[]).map(payment => ({
    ...payment,
    customer: payment.customer,
    card: payment.card,
  }));
}

export async function findOrCreateCard(
  customerId: string,
  cardType: CardType
): Promise<Card> {
  const cards = await getCustomerCards(customerId);
  const existing = cards.find(c => c.card_type === cardType);
  if (existing) return existing;

  const defaultLimit = CARD_TYPE_DEFAULTS[cardType] || 0;
  return createCard(customerId, cardType, defaultLimit);
}

export async function getUserPreferences(): Promise<UserPreferences | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('user_preferences')
    .select('*')
    .eq('user_id', user.id)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function upsertUserPreferences(
  prefs: Partial<Omit<UserPreferences, 'id' | 'user_id' | 'created_at' | 'updated_at'>>
): Promise<UserPreferences> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const existing = await getUserPreferences();

  if (existing) {
    const { data, error } = await supabase
      .from('user_preferences')
      .update({ ...prefs, updated_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .select()
      .single();
    if (error) throw error;
    return data;
  }

  const { data, error } = await supabase
    .from('user_preferences')
    .insert({ user_id: user.id, ...prefs })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function exportPaymentsCsv(businessId: string): Promise<string> {
  const payments = await getPayments(businessId);
  const header = 'Date,Customer,Amount USD,Stripe Charge ID';
  const rows = payments.map(p =>
    `${p.payment_date},"${p.customer?.name || ''}",${p.amount_usd},${p.stripe_charge_id || ''}`
  );
  return [header, ...rows].join('\n');
}

export async function exportExpensesCsv(businessId: string): Promise<string> {
  const expenses = await getExpenses(businessId);
  const header = 'Date,Category,Amount MVR,Note';
  const rows = expenses.map(e =>
    `${e.expense_date},${e.category},${e.amount_mvr},"${e.note || ''}"`
  );
  return [header, ...rows].join('\n');
}

export async function exportCustomersCsv(businessId: string): Promise<string> {
  const customers = await getCustomers(businessId);
  const header = 'Name,Email,Phone,Cards';
  const rows = customers.map(c =>
    `"${c.name}",${c.email || ''},${c.phone || ''},${c.cards.length}`
  );
  return [header, ...rows].join('\n');
}

export async function exportAllDataJson(businessId: string): Promise<string> {
  const [payments, expenses, customers] = await Promise.all([
    getPayments(businessId),
    getExpenses(businessId),
    getCustomers(businessId),
  ]);
  return JSON.stringify({ payments, expenses, customers, exportedAt: new Date().toISOString() }, null, 2);
}
